/**
 * OS-1712 — validación de NIT y subdominio en el wizard de alta, antes de enviar.
 * El API repite ambas validaciones (fuente de verdad); aquí solo se adelanta el mensaje.
 */

/**
 * Pesos DIAN de derecha a izquierda (módulo 11). Mismo algoritmo que
 * `apps/api/src/shared/pila/pila-master-data.util.ts` (`computeDianVerificationDigit`): la consola
 * es una app aparte y no puede importar código del API.
 */
const DIAN_NIT_WEIGHTS = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71] as const;

export function computeNitVerificationDigit(nitBase: string): string {
  let sum = 0;
  for (let i = 0; i < nitBase.length; i += 1) {
    sum += Number(nitBase.charAt(nitBase.length - 1 - i)) * (DIAN_NIT_WEIGHTS[i] ?? 0);
  }
  const remainder = sum % 11;
  return String(remainder < 2 ? remainder : 11 - remainder);
}

export type NitCheck =
  | { ok: true; nit: string; verificationDigit: string }
  | { ok: false; message: string };

/**
 * Acepta `900123456-8` o `900.123.456-8`. Exige el DV (con guion) para no confundir un NIT de
 * 10 dígitos con uno de 9 + DV.
 */
export function checkNit(raw: string): NitCheck {
  const value = raw.trim();
  const match = /^([\d.\s]+)(?:-\s*(\d))?$/.exec(value);
  const base = match?.[1]?.replace(/\D/g, '') ?? '';
  if (!match || !/^\d{3,15}$/.test(base)) {
    return { ok: false, message: 'El NIT debe tener entre 3 y 15 dígitos, p. ej. 900123456-8.' };
  }
  const expected = computeNitVerificationDigit(base);
  const digit = match[2];
  if (digit === undefined) {
    return {
      ok: false,
      message: `Incluye el dígito de verificación después de un guion: ${base}-${expected}.`,
    };
  }
  if (digit !== expected) {
    return {
      ok: false,
      message: `El dígito de verificación no corresponde a este NIT (debería ser ${expected}).`,
    };
  }
  return { ok: true, nit: base, verificationDigit: digit };
}

export const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/;

/** Solo minúsculas, dígitos y guiones, como se escribe en el campo. */
export function sanitizeSubdomain(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9-]/g, '');
}

export function subdomainFormatMessage(subdomain: string): string | null {
  if (subdomain === '' || SUBDOMAIN_PATTERN.test(subdomain)) {
    return null;
  }
  return 'Usa de 3 a 63 caracteres: letras minúsculas, números y guiones (sin empezar ni terminar en guion).';
}
